import { useLocation } from "wouter";
import { ChevronRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useInsureds } from "@/lib/api";
import type { AugmentedInsuredRow, InsuredStatusFilter, InsuredSortField, SortDir } from "@shared/pagination";
import { formatInitials, formatCurrencyCompact } from "./dashboard-helpers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  // CSR-674: discriminator instead of pre-built summaries. The dialog
  // fetches its own data via /api/insureds (pageSize=100). When status
  // is null, the dialog renders the empty state without fetching.
  status: InsuredStatusFilter | null;
  agencyId?: string;
  emptyMessage?: string;
  // CSR-674 R-002: caller can pin a sort key; defaults to the server's
  // locked default (MIN(p.effective_date) DESC NULLS LAST). The
  // Dashboard "Premium Contributors" card passes ("premium", "desc") so
  // the row order matches the card's promise.
  sort?: InsuredSortField;
  sortDir?: SortDir;
}

const cycleLabel = (insured: AugmentedInsuredRow): string => {
  if (insured.unmatched) return "Unmatched";
  if (insured.activePolicyCount > 0 && insured.hasUpcomingRenewal) return "Renewal";
  if (insured.activePolicyCount > 0) return "Active Customer";
  if (insured.hasRecentLoss) return "At Risk";
  return "Inactive";
};

export function InsuredsListDialog({
  open,
  onOpenChange,
  title,
  description,
  status,
  agencyId,
  emptyMessage = "No insureds match this segment yet.",
  sort,
  sortDir,
}: Props) {
  const [, navigate] = useLocation();

  const enabled = open && status !== null;
  const { data, isLoading } = useInsureds({
    agencyId,
    status: status ?? undefined,
    pageSize: 100,
    page: 1,
    sort,
    sortDir,
    enabled,
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid="insureds-list-dialog"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {status === null ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        ) : isLoading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          <>
            <div className="space-y-1">
              {rows.map((insured) => {
                const name = insured.name || insured.commercialName || "—";
                const policyCount = insured.policyCount;
                // CSR-674 R-002: when the caller sorted by premium, surface
                // the premium so users can see the field they're sorted by.
                const showPremium = sort === "premium";
                if (!insured.id) {
                  // Orphan row: not navigable.
                  return (
                    <div
                      key={`unmatched:${name}`}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border border-transparent text-left"
                      data-testid={`insured-list-row-unmatched`}
                    >
                      <div className="w-9 h-9 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-semibold shrink-0">
                        {formatInitials(name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {cycleLabel(insured)} · {policyCount} {policyCount === 1 ? "policy" : "policies"}
                        </p>
                      </div>
                      {showPremium && (
                        <div className="text-xs text-muted-foreground shrink-0">
                          {formatCurrencyCompact(insured.activePremium)}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <button
                    key={insured.id}
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      navigate(`/insureds/${insured.id}`);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-transparent hover:bg-muted/50 hover:border-border cursor-pointer transition-colors text-left"
                    data-testid={`insured-list-row-${insured.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-semibold shrink-0">
                      {formatInitials(name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {cycleLabel(insured)} · {policyCount} {policyCount === 1 ? "policy" : "policies"}
                      </p>
                    </div>
                    {showPremium && (
                      <div className="text-xs font-medium text-muted-foreground shrink-0">
                        {formatCurrencyCompact(insured.activePremium)}
                      </div>
                    )}
                    <div className="flex items-center gap-2 shrink-0">
                      <ChevronRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="pt-3 text-xs text-muted-foreground">
              {total > 100 ? (
                <>
                  Showing first 100 of {total.toLocaleString()} — see <a className="underline" href="/insureds">/insureds</a> for the full list
                </>
              ) : (
                <>{rows.length} {rows.length === 1 ? "record" : "records"} shown</>
              )}
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
